/**
 * freezeAttribution — turns "a freeze happened" into "a freeze happened BECAUSE
 * X was churning" (2026-06-27).
 *
 * The always-on FreezeSentinel detects rAF-gap freezes but historically recorded
 * only {kind, stallMs, route, heapUsedMB} — never the cause. Diagnosing one meant
 * hand-driving AllocCounters or the Firefox profiler. This module is the
 * always-on collector the sentinel snapshots at freeze time:
 *
 *   - AllocCounters (already instrumented across the hot paths) is enabled and
 *     sampled on a ~1s cadence; on a freeze we diff the last two samples → the
 *     top per-second movers. Cross-browser, and exactly what exposed the
 *     NetworkSyncSystem onChange leak (schema.onChange.npc 24,705/s).
 *   - A `longtask` PerformanceObserver (Chromium) keeps a small ring of recent
 *     long tasks; on a freeze we surface any overlapping the gap.
 *   - Heap level is sampled cheaply so the sentinel can report the delta across
 *     the gap (Chromium) — a large drop ⇒ GC reclaimed mid-stall.
 *
 * Overhead is negligible (a handful of int increments/sec + one native observer)
 * and it is strictly diagnostic — nothing here feeds gameplay logic.
 */

import { AllocCounters } from './AllocCounters';
import { FramePhaseBudget } from './FramePhaseBudget';
import type {
  FreezeAttribution,
  FreezeLoafAttribution,
  FreezeLoafScript,
} from '../../shared/diagnostics/FreezeEventTypes';

/** How long a window the counter diff covers (ms). */
const SAMPLE_INTERVAL_MS = 1000;
/** Keep at most this many recent longtasks (ring). */
const LONGTASK_RING = 32;
/** Keep at most this many recent Long Animation Frames (ring). */
const LOAF_RING = 32;
/** Max attributed scripts kept per LoAF entry (bounded for the wire schema). */
const LOAF_SCRIPTS = 4;
/** Default number of top movers reported. */
const TOP_N = 5;
/** setTimeout cadence for event-loop lag sampling (ms). */
const LAG_SAMPLE_MS = 50;
/** Number of lag samples to retain (~2s window). */
const LAG_RING = 40;
/**
 * Ignore single samples delayed more than this (ms). The sampler already skips
 * hidden documents, so this only ever fires on a visible tab — where a delay
 * this large IS the freeze we are trying to explain. It was 500ms, which threw
 * away the evidence for every stall above half a second. Raised to match
 * FREEZE_CONFIG.DEBUGGER_PAUSE_CEILING_MS, the point past which a pause really
 * is a debugger/OS-sleep artifact.
 */
const LAG_IGNORE_THRESHOLD_MS = 5000;

interface CounterSample {
  at: number; // performance.now()
  counters: Record<string, number>;
}

interface LongTaskEntry {
  start: number; // performance.now() timebase
  duration: number;
}

/** Shape of `PerformanceLongAnimationFrameTiming` — not in the TS DOM lib yet.
 *  Every field is optional because older Chromium ships partial support. */
export interface LoafEntryLike {
  startTime: number;
  duration: number;
  renderStart?: number;
  styleAndLayoutStart?: number;
  blockingDuration?: number;
  scripts?: {
    duration?: number;
    invoker?: string;
    invokerType?: string;
    sourceURL?: string;
    sourceFunctionName?: string;
  }[];
}

/** Bounded LoAF entry retained in the ring. */
interface LoafRecord extends FreezeLoafAttribution {
  start: number;
}

// ── Pure, unit-tested helpers ───────────────────────────────────────

/**
 * PerformanceObserver logs a browser warning before (and sometimes instead
 * of) throwing when asked to observe an unsupported entry type. Gate the
 * registration against the browser-owned capability list so Firefox keeps the
 * counter/event-loop fallback without noisy invalid registrations.
 */
export function supportsPerformanceEntryType(
  entryType: string,
  supportedEntryTypes: readonly string[] | undefined,
): boolean {
  return supportedEntryTypes?.includes(entryType) === true;
}

/**
 * Top per-second movers between two counter snapshots. Only positive deltas;
 * sorted desc; capped at `n`. Returns [] for a non-positive window.
 */
export function computeTopMovers(
  prev: Record<string, number>,
  curr: Record<string, number>,
  dtMs: number,
  n: number = TOP_N,
): { key: string; perSec: number }[] {
  if (dtMs <= 0) return [];
  const out: { key: string; perSec: number }[] = [];
  for (const key of Object.keys(curr)) {
    const delta = curr[key] - (prev[key] ?? 0);
    if (delta > 0) out.push({ key, perSec: Math.round((delta / dtMs) * 1000) });
  }
  out.sort((a, b) => b.perSec - a.perSec);
  return out.slice(0, n);
}

/**
 * Reduce a Long Animation Frame to bounded primitives. Splits the frame into
 * script time vs render (style/layout/paint) time so a reader can tell "our JS
 * is slow" from "our rendering approach is slow" without opening a profiler.
 *
 * A script is named by `sourceFunctionName`, falling back to `sourceURL` then
 * `invoker`, because Chromium populates them unevenly depending on how the work
 * was scheduled (event listener vs promise vs module evaluation).
 */
export function summarizeLoaf(entry: LoafEntryLike, n: number = LOAF_SCRIPTS): FreezeLoafAttribution {
  const end = entry.startTime + entry.duration;
  const renderStart = entry.renderStart ?? 0;
  const styleStart = entry.styleAndLayoutStart ?? 0;
  const scripts: FreezeLoafScript[] = (entry.scripts ?? [])
    .map((s) => ({
      name: (s.sourceFunctionName || s.sourceURL || s.invoker || 'anonymous').slice(0, 120),
      invokerType: (s.invokerType || 'unknown').slice(0, 40),
      durationMs: Math.round(s.duration ?? 0),
    }))
    .sort((a, b) => b.durationMs - a.durationMs)
    .slice(0, n);
  return {
    durationMs: Math.round(entry.duration),
    blockingDurationMs: Math.round(entry.blockingDuration ?? 0),
    scriptMs: Math.round((entry.scripts ?? []).reduce((sum, s) => sum + (s.duration ?? 0), 0)),
    renderMs: renderStart > 0 ? Math.max(0, Math.round(end - renderStart)) : 0,
    styleAndLayoutMs: styleStart > 0 ? Math.max(0, Math.round(end - styleStart)) : 0,
    scripts,
  };
}

/** Thresholds for the "rAF throttled by browser" verdict. When the Pixi ticker,
 *  scene update, and event-loop heartbeat are all fast but the rAF gap is large,
 *  the browser/OS is pausing frames (occluded window, display sleep, power
 *  saving) rather than our code stalling. */
const RAF_THROTTLE_RENDER_MS = 2;
const RAF_THROTTLE_TICK_MS = 2;
const RAF_THROTTLE_LAG_MS = 50;

/**
 * Coarse verdict. A sharp heap reclaim can mean GC, but GC is a main-thread
 * pause so it must show up as a longtask or event-loop lag. If the heap drops
 * but all measured JS paths are fast and the event loop is not delayed, the GC
 * happened during a browser rAF pause (incidental). Otherwise: a longtask /
 * event-loop lag covering most of the gap ⇒ real JS work.
 *
 * "Frames stopped while the event loop stayed healthy" is the interesting case,
 * and it splits on focus (2026-07-25):
 *
 *  - NOT focused ⇒ `raf-throttled`. The browser genuinely deprioritises frames
 *    for occluded/backgrounded windows. Benign; needs containment, not a fix.
 *  - focused ⇒ `presentation-stall`. Frames are not reaching the screen while
 *    JS is idle, which is what a compositor/GPU present hang looks like from
 *    inside the page. The 2026-07-25 Chrome trace measured exactly this as
 *    43 s of `Commit` in a 45 s window.
 *
 * That split matters most on Firefox, which supports neither `longtask` nor
 * LoAF: a real freeze there has no other evidence to land on, and calling it
 * "throttled by the browser" reads as benign when it is the open bug.
 */
export function classifyFreezeSource(
  longTasksMs: number[],
  heapDeltaMB: number | null,
  eventLoopLagMs: number,
  gapMs: number,
  lastTickerMs?: number,
  sceneTickMs?: number,
  focused?: boolean,
): FreezeAttribution['source'] {
  const longest = longTasksMs.length ? Math.max(...longTasksMs) : 0;
  // GC must block the main thread for a meaningful portion of the gap. A heap
  // drop without matching main-thread delay is just the browser collecting
  // during an rAF pause.
  const gcEvidenceThreshold = Math.max(50, gapMs * 0.25);
  if (
    heapDeltaMB !== null &&
    heapDeltaMB <= -4 &&
    (longest >= gcEvidenceThreshold || eventLoopLagMs >= gcEvidenceThreshold)
  ) {
    return 'gc-suspected';
  }
  if (longest >= gapMs * 0.5 || eventLoopLagMs >= gapMs * 0.5) return 'js-longtask';
  if (
    gapMs >= 100 &&
    (lastTickerMs === undefined || lastTickerMs < RAF_THROTTLE_RENDER_MS) &&
    (sceneTickMs === undefined || sceneTickMs < RAF_THROTTLE_TICK_MS) &&
    eventLoopLagMs < RAF_THROTTLE_LAG_MS
  ) {
    // Unknown focus stays conservative: report throttling rather than accuse the
    // presentation path on evidence we do not have.
    return focused === true ? 'presentation-stall' : 'raf-throttled';
  }
  return 'unknown';
}

// ── Event-loop lag sampler ──────────────────────────────────────────

/**
 * Measures how late setTimeout callbacks actually fire. This catches event-loop
 * congestion caused by many short tasks (e.g. React Scheduler slices, frequent
 * state patches) that the PerformanceObserver longtask API cannot see on any
 * browser, and is especially useful on Firefox where longtasks are unsupported.
 */
class EventLoopLagSampler {
  private samples: { at: number; lagMs: number }[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private expected = 0;

  start(): void {
    this.stop();
    this.expected = performance.now() + LAG_SAMPLE_MS;
    this.timer = setTimeout(() => { this.tick(); }, LAG_SAMPLE_MS);
  }

  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private tick(): void {
    const now = performance.now();
    const lagMs = now - this.expected;
    this.expected = now + LAG_SAMPLE_MS;
    // Skip background-tab throttles (setTimeout can fire at ~1s when hidden).
    if (typeof document !== 'undefined' && !document.hidden && lagMs < LAG_IGNORE_THRESHOLD_MS) {
      this.samples.push({ at: now, lagMs: Math.max(0, lagMs) });
      const cutoff = now - (LAG_SAMPLE_MS * LAG_RING);
      while (this.samples.length > 0 && this.samples[0].at < cutoff) {
        this.samples.shift();
      }
    }
    this.timer = setTimeout(() => { this.tick(); }, LAG_SAMPLE_MS);
  }

  /** Max lag in the last `windowMs` (defaults to full ring). */
  getMaxLag(windowMs = LAG_SAMPLE_MS * LAG_RING): number {
    const now = performance.now();
    const cutoff = now - windowMs;
    let max = 0;
    for (const s of this.samples) {
      if (s.at >= cutoff) max = Math.max(max, s.lagMs);
    }
    return Math.round(max);
  }
}

// ── Stateful collector ──────────────────────────────────────────────

export class FreezeAttributor {
  private prevSample: CounterSample | null = null;
  private currSample: CounterSample | null = null;
  private longTasks: LongTaskEntry[] = [];
  private loafs: LoafRecord[] = [];
  private observer: PerformanceObserver | null = null;
  private loafObserver: PerformanceObserver | null = null;
  private lastHeapMB: number | null = null;
  private lagSampler = new EventLoopLagSampler();

  /** Start collecting. Idempotent; safe in browser-less envs (no-ops the bits
   *  that need DOM/PerformanceObserver). */
  start(): void {
    // AllocCounters is gated off by default (a bare bool check per inc when
    // disabled); enable so we always have hot-path attribution ready at freeze
    // time. Idempotent — the manual Playwright probe's enable()/reset() coexist.
    try { AllocCounters.enable(); } catch { /* noop */ }
    this.sampleCounters(performance.now());
    try { this.lagSampler.start(); } catch { /* noop */ }

    try {
      if (
        typeof PerformanceObserver === 'function'
        && supportsPerformanceEntryType('longtask', PerformanceObserver.supportedEntryTypes)
      ) {
        this.observer = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            this.longTasks.push({ start: e.startTime, duration: e.duration });
          }
          if (this.longTasks.length > LONGTASK_RING) {
            this.longTasks.splice(0, this.longTasks.length - LONGTASK_RING);
          }
        });
        this.observer.observe({ entryTypes: ['longtask'] });
      }
    } catch { /* longtask unsupported (Firefox/Safari) — counters still work */ }

    try {
      if (
        typeof PerformanceObserver === 'function'
        && supportsPerformanceEntryType('long-animation-frame', PerformanceObserver.supportedEntryTypes)
      ) {
        this.loafObserver = new PerformanceObserver((list) => {
          for (const e of list.getEntries()) {
            const raw = e as unknown as LoafEntryLike;
            this.loafs.push({ start: raw.startTime, ...summarizeLoaf(raw) });
          }
          if (this.loafs.length > LOAF_RING) {
            this.loafs.splice(0, this.loafs.length - LOAF_RING);
          }
        });
        // `durationThreshold` keeps the ring on real stalls rather than every
        // slightly-late frame. 100ms is well under our 150ms freeze threshold,
        // so any frame that can produce a freeze is retained.
        this.loafObserver.observe({
          type: 'long-animation-frame',
          buffered: true,
          durationThreshold: 100,
        } as PerformanceObserverInit);
      }
    } catch { /* LoAF unsupported (<Chromium 123, Firefox, Safari) */ }
  }

  stop(): void {
    try { this.observer?.disconnect(); } catch { /* noop */ }
    this.observer = null;
    try { this.loafObserver?.disconnect(); } catch { /* noop */ }
    this.loafObserver = null;
    try { this.lagSampler.stop(); } catch { /* noop */ }
  }

  private readHeapMB(): number | null {
    const perf = (globalThis as { performance?: Performance & { memory?: { usedJSHeapSize: number } } }).performance;
    const used = perf?.memory?.usedJSHeapSize;
    return typeof used === 'number' && Number.isFinite(used) ? Math.round(used / 1048576) : null;
  }

  /** Snapshot the live counters into the rolling prev/curr pair. Called ~1Hz by
   *  the sentinel loop; cheap (a shallow copy of a small fixed counter set). */
  sampleCounters(now: number): void {
    const counters = AllocCounters.getStats().counters;
    this.prevSample = this.currSample;
    this.currSample = { at: now, counters };
  }

  /** Called every frame by the sentinel so the heap "before the gap" is the last
   *  healthy frame's level. Cheap single getter read. */
  noteFrameHeap(): void {
    this.lastHeapMB = this.readHeapMB();
  }

  /** True once enough time has elapsed to warrant a fresh counter sample. */
  shouldSample(now: number): boolean {
    return !this.currSample || now - this.currSample.at >= SAMPLE_INTERVAL_MS;
  }

  /**
   * Build the attribution for a freeze of `gapMs` detected at `nowPerf`
   * (performance.now()). `heapBeforeMB` defaults to the last frame's heap.
   */
  snapshot(
    gapMs: number,
    nowPerf: number,
    opts?: { lastTickerMs?: number; sceneTickMs?: number; focused?: boolean },
    n: number = TOP_N,
  ): FreezeAttribution {
    // Counter movers over the last completed window (prev → curr). Fall back to
    // curr-vs-empty if we only have one sample yet.
    const prev = this.prevSample?.counters ?? {};
    const curr = this.currSample?.counters ?? AllocCounters.getStats().counters;
    const dtMs = this.prevSample && this.currSample
      ? this.currSample.at - this.prevSample.at
      : SAMPLE_INTERVAL_MS;
    const topCounters = computeTopMovers(prev, curr, dtMs, n);

    // Measured time ownership. Unlike topCounters (frequency), this ranking is
    // actionable per the 2026-07-22 incident's attribution rules.
    const phaseBudgetMs = FramePhaseBudget.drain(dtMs);

    // Longtasks overlapping the gap [nowPerf - gapMs, nowPerf].
    const gapStart = nowPerf - gapMs;
    const longTasksMs = this.longTasks
      .filter((t) => t.start + t.duration >= gapStart && t.start <= nowPerf)
      .map((t) => Math.round(t.duration));

    const heapNow = this.readHeapMB();
    const heapDeltaMB = heapNow !== null && this.lastHeapMB !== null
      ? heapNow - this.lastHeapMB
      : null;
    // Window the lag to THIS gap. `getMaxLag()` defaults to the full ~2s ring,
    // and `classifyFreezeSource` compares the result against `gapMs * 0.5`, so
    // any unrelated stall in the trailing 2s made every freeze in that window
    // read `js-longtask`. That is how 141/162 focused freezes were reported as
    // JS on 2026-07-25. Floor at two sample periods so a short gap still has at
    // least one sample to look at.
    const eventLoopLagMs = this.lagSampler.getMaxLag(Math.max(gapMs, LAG_SAMPLE_MS * 2));

    // The largest Long Animation Frame overlapping the gap. Largest rather than
    // nearest: a freeze can span several late frames, and the one that owns the
    // most wall-clock is the one worth naming.
    const loaf = this.loafs
      .filter((l) => l.start + l.durationMs >= gapStart && l.start <= nowPerf)
      .sort((a, b) => b.durationMs - a.durationMs)[0];

    return {
      topCounters,
      phaseBudgetMs,
      longTasksMs,
      heapDeltaMB,
      eventLoopLagMs,
      ...(loaf ? { loaf: {
        durationMs: loaf.durationMs,
        blockingDurationMs: loaf.blockingDurationMs,
        scriptMs: loaf.scriptMs,
        renderMs: loaf.renderMs,
        styleAndLayoutMs: loaf.styleAndLayoutMs,
        scripts: loaf.scripts,
      } } : {}),
      source: classifyFreezeSource(
        longTasksMs, heapDeltaMB, eventLoopLagMs, gapMs,
        opts?.lastTickerMs, opts?.sceneTickMs, opts?.focused,
      ),
    };
  }
}
